import IconArrowDown from '@listening/assets/astro/icons/icon-arrow-down.svg?react';
import sliderFolders from '@listening/assets/astro/images/new-design-25/ui/slider-folder.png';
import mobileAppImage from '@listening/assets/astro/images/new-design-25/ui/slider-mobile-overview.png';
import sliderReading from '@listening/assets/astro/images/new-design-25/ui/slider-reading.png';
import sliderScan from '@listening/assets/astro/images/new-design-25/ui/slider-scan.png';
import sliderUpload from '@listening/assets/astro/images/new-design-25/ui/slider-upload.png';
import { Button } from '@listening/frontend/components/radix/button';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsClient } from 'usehooks-ts';
import { useCtaHref } from '@/components/astro/cta-button/cta-button.tsx';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import * as m from '@/paraglide/messages';

function getFeatures() {
  return [
    {
      id: 0,
      title: m.app_slider_easy_to_use(),
      image: mobileAppImage,
      alt: 'Easy to use view',
    },
    {
      id: 1,
      title: m.app_slider_scan_papers(),
      image: sliderScan,
      alt: 'Scan papers view',
    },
    {
      id: 2,
      title: m.app_slider_upload_documents(),
      image: sliderUpload,
      alt: 'Upload documents view',
    },
    {
      id: 3,
      title: m.app_slider_reading_view(),
      image: sliderReading,
      alt: 'Reading view',
    },
    {
      id: 4,
      title: m.app_slider_folders(),
      image: sliderFolders,
      alt: 'Folders view',
    },
  ];
}

export function AppSlider() {
  const { isInFreePlanCountry } = useFreePlan();
  const ctaHref = useCtaHref();
  const features = getFeatures();
  const sectionRef = useRef<HTMLElement>(null);
  const tryButtonRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSliderVisible, setIsSliderVisible] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(false);
  const isMounted = useIsClient();
  const progressHeight = 100 / features.length;

  /**
   * Determine slider text & nav visibility
   * (You may want to refine or remove these checks if
   *  they're still causing layout shifts at extreme zoom.)
   */
  useEffect(() => {
    const handleScroll = () => {
      if (!(tryButtonRef.current && sectionRef.current)) return;

      const buttonRect = tryButtonRef.current.getBoundingClientRect();
      const sectionRect = sectionRef.current.getBoundingClientRect();

      // Slider text visibility
      const isSliderVis =
        sectionRect.top < window.innerHeight * 0.8 &&
        sectionRect.bottom > 0 &&
        (window.innerWidth >= 768 ||
          (buttonRect.top > window.innerHeight * 0.8 &&
            sectionRect.top < window.innerHeight * 0.4));

      // Navigation buttons visibility
      const isNavVis =
        sectionRect.top < window.innerHeight * 0.6 &&
        buttonRect.top > window.innerHeight * 0.6 &&
        (window.innerWidth >= 768 ||
          sectionRect.top < window.innerHeight * 0.4);

      setIsSliderVisible(isSliderVis);
      setIsNavVisible(isNavVis);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Click a slide => scroll that slide into view
  const handleSlideClick = (index: number) => {
    setActiveIndex(index);
    const slide = document.querySelector(`[data-index="${String(index)}"]`);
    if (slide) {
      slide.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  // Track which slide is nearest the viewport center
  useEffect(() => {
    const handleScroll = () => {
      const slides = document.querySelectorAll('[data-index]');
      const viewportMiddle = window.innerHeight * 0.5;
      let closestSlide = { index: 0, distance: Number.POSITIVE_INFINITY };

      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideMiddle = rect.top + rect.height / 2;
        const distance = Math.abs(slideMiddle - viewportMiddle);

        if (distance < closestSlide.distance) {
          closestSlide = { index, distance };
        }
      });

      // If the closest slide is reasonably close to center, update activeIndex
      if (closestSlide.distance < window.innerHeight * 0.4) {
        setActiveIndex(closestSlide.index);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll to first slide
  const scrollToTop = () => {
    setActiveIndex(0);
    const firstSlide = document.querySelector('[data-index="0"]');
    if (firstSlide) {
      firstSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  // Scroll to last slide
  const scrollToBottom = () => {
    setActiveIndex(features.length - 1);
    const lastSlide = document.querySelector(
      `[data-index="${String(features.length - 1)}"]`,
    );
    if (lastSlide) {
      lastSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <section
      aria-label='App Features Slider'
      className='relative min-h-dvh bg-white'
      ref={sectionRef}
    >
      <div className='top-0 z-10 bg-white px-4 pt-12 sm:px-6'>
        <div className='text-center'>
          <h2 className='text-4xl font-bold md:text-5xl' id='slider-heading'>
            {m.app_slider_heading()}
          </h2>
        </div>
      </div>

      <div className='relative mx-auto max-w-[1300px] px-4 pt-[60px]'>
        <div className='relative grid grid-cols-1 gap-8 md:grid-cols-[1.5fr_auto_0.8fr] md:gap-12'>
          {/* Interactive text buttons */}
          <div
            aria-label='Feature navigation'
            className={`fixed inset-x-0 bottom-8 z-50 order-2 transition-opacity duration-300 md:relative md:bottom-auto md:order-1 [&::-webkit-scrollbar]:hidden ${isSliderVisible ? 'opacity-100' : 'pointer-events-none opacity-0'} `}
            role='tablist'
          >
            <div className='relative flex md:sticky md:top-[25vh] md:flex-col md:items-end md:space-y-6 lg:space-y-8'>
              {/* Mobile version */}
              <div className='relative w-full overflow-visible md:hidden'>
                <div
                  className='flex items-center'
                  style={{
                    transform: `translateX(calc(50vw - ${String(
                      activeIndex * 100,
                    )}vw - 50%))`,
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {features.map((feature, index) => (
                    <div
                      className='flex w-screen shrink-0 justify-center'
                      key={feature.id}
                    >
                      <button
                        className={`rounded-full px-6 py-2 text-center text-xl font-bold transition-all duration-300 ${
                          index === activeIndex
                            ? 'scale-110 bg-white font-black text-blue-600 shadow-md'
                            : 'scale-90 text-gray-400'
                        } `}
                        onClick={() => {
                          handleSlideClick(index);
                        }}
                        type='button'
                      >
                        {feature.title}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop version */}
              <div className='hidden whitespace-nowrap md:flex md:flex-col md:items-end md:space-y-6 lg:space-y-8'>
                {features.map((feature, index) => (
                  <button
                    aria-controls={`slide-${String(feature.id)}`}
                    aria-selected={index === activeIndex}
                    className={`text-right text-2xl font-bold text-nowrap transition-all duration-300 hover:text-blue-600 lg:text-3xl xl:text-4xl ${
                      index === activeIndex
                        ? 'font-black text-black'
                        : 'text-gray-400'
                    } `}
                    key={feature.id}
                    onClick={() => {
                      handleSlideClick(index);
                    }}
                    role='tab'
                    type='button'
                  >
                    {feature.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Slider content */}
          <div
            aria-labelledby='slider-heading'
            className='relative order-1 md:order-2'
            role='tabpanel'
          >
            <div className='sticky top-[10vh] flex justify-center'>
              <div className='xs:w-[260px] xs:[height:min(420px,65vh)] h-[min(380px,60vh)] w-[220px] overflow-hidden rounded-2xl sm:h-[min(500px,70vh)] sm:w-[280px] md:h-[min(600px,75vh)] md:w-[300px] md:rounded-3xl lg:h-[min(650px,80vh)] lg:w-[320px] xl:h-[min(680px,85vh)] xl:w-[340px]'>
                {features.map((feature, index) => (
                  <div
                    aria-hidden={index !== activeIndex}
                    className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                      index === activeIndex
                        ? 'translate-y-0 opacity-100'
                        : index < activeIndex
                          ? '-translate-y-8 opacity-0'
                          : 'translate-y-8 opacity-0'
                    }`}
                    id={`slide-${String(feature.id)}`}
                    key={feature.id}
                    role='tabpanel'
                  >
                    <div className='size-full rounded-2xl bg-white md:rounded-3xl'>
                      <img
                        alt={feature.alt}
                        className='size-full object-contain p-2'
                        height={800}
                        loading='lazy'
                        src={feature.image}
                        width={380}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Invisible scroll markers */}
            {features.map((feature) => (
              <div
                className='h-[75vh] snap-start snap-always'
                data-index={feature.id}
                key={feature.id}
              />
            ))}
          </div>

          {/* Progress indicator */}
          <div className='relative order-3 hidden md:block'>
            <div className='sticky top-[25vh] flex h-[300px] items-center'>
              <div className='relative h-full w-1.5'>
                <div className='absolute inset-0 rounded-full bg-gray-200'>
                  <motion.div
                    animate={{
                      height: `${String(progressHeight)}%`,
                      top: `${String(activeIndex * progressHeight)}%`,
                    }}
                    className='absolute w-full rounded-full bg-blue-600'
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div
          aria-label='Slider navigation'
          className={`fixed inset-x-0 bottom-24 z-50 flex flex-row justify-center gap-4 transition-opacity duration-300 lg:top-1/2 lg:right-8 lg:bottom-auto lg:left-auto lg:-translate-y-1/2 lg:transform lg:flex-col lg:items-center lg:justify-center ${isMounted ? (isNavVisible ? 'opacity-100' : 'pointer-events-none opacity-0') : 'opacity-0'} `}
          role='navigation'
        >
          <button
            aria-label='Go to first slide'
            className='rounded-full bg-white p-3 shadow-lg transition-colors hover:bg-gray-50'
            onClick={scrollToTop}
            type='button'
          >
            <IconArrowDown className='size-6 rotate-180' />
          </button>
          <button
            aria-label='Go to last slide'
            className='rounded-full bg-white p-3 shadow-lg transition-colors hover:bg-gray-50'
            onClick={scrollToBottom}
            type='button'
          >
            <IconArrowDown className='size-6' />
          </button>
        </div>
      </div>

      {/* Try Now Button */}
      <div
        className='z-100 mt-20 flex w-full justify-center pb-20'
        ref={tryButtonRef}
      >
        <Button
          aria-label='Try app for free'
          asChild
          className='relative rounded-full bg-royal-50 px-6 py-4 text-base font-medium text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:px-24 sm:py-8 sm:text-lg'
          variant='ghost'
        >
          <Link rel='noopener noreferrer' target='_blank' to={ctaHref}>
            {isInFreePlanCountry ? m.app_slider_cta_free() : m.app_slider_cta()}
          </Link>
        </Button>
      </div>
    </section>
  );
}

export function AppSliderMobile() {
  const { isInFreePlanCountry } = useFreePlan();
  const ctaHref = useCtaHref();
  const features = getFeatures();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Function to handle index changes with wrapping
  const nextSlide = useCallback(() => {
    setActiveIndex((current) => (current + 1) % features.length);
  }, [features.length]);

  // Set up intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]) {
          setIsVisible(entries[0].isIntersecting);
        }
      },
      {
        threshold: 0.2, // Start when 20% of the component is visible
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-sliding functionality
  useEffect(() => {
    if (isPaused || !isVisible) return;

    const intervalId = setInterval(nextSlide, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isPaused, isVisible, nextSlide]);

  // Handle manual slide changes
  const handleSlideClick = (index: number) => {
    setActiveIndex(index);
    setIsPaused(true);

    setTimeout(() => {
      setIsPaused(false);
    }, 5000);
  };

  return (
    <section
      aria-label='App Features'
      className='relative min-h-dvh bg-white'
      ref={sectionRef}
    >
      <div className='top-0 z-10 bg-white px-4 pt-12 sm:px-6'>
        <div className='text-center'>
          <h2 className='mb-4 text-4xl font-bold' id='slider-heading'>
            {m.app_slider_heading()}
          </h2>
        </div>
      </div>

      <div className='mt-6 flex flex-col items-center px-4'>
        {/* Feature titles column */}
        <div className='flex w-full flex-col space-y-6'>
          {features.map((feature, index) => (
            <button
              className={`text-center text-xl font-bold transition-all duration-300 ${
                index === activeIndex
                  ? 'scale-110 text-blue-600'
                  : 'text-gray-400'
              }`}
              key={feature.id}
              onClick={() => {
                handleSlideClick(index);
              }}
              type='button'
            >
              {feature.title}
            </button>
          ))}
        </div>

        {/* Image container */}
        <div className='relative mt-12 h-[500px] w-[260px] overflow-hidden rounded-2xl'>
          {features.map((feature, index) => (
            <div
              className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                index === activeIndex
                  ? 'translate-y-0 opacity-100'
                  : index < activeIndex
                    ? '-translate-y-full opacity-0'
                    : 'translate-y-full opacity-0'
              }`}
              key={feature.id}
            >
              <div className='size-full rounded-2xl bg-white'>
                <img
                  alt={feature.alt}
                  className='size-full object-contain p-2'
                  height={800}
                  loading='lazy'
                  src={feature.image}
                  width={380}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Try Now Button */}
        <div className='z-100 mt-20 flex w-full justify-center pb-20'>
          <Button
            aria-label='Try app for free'
            asChild
            className='relative rounded-full bg-royal-50 px-6 py-4 text-base font-medium text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:px-24 sm:py-8 sm:text-lg'
            variant='ghost'
          >
            <Link rel='noopener noreferrer' target='_blank' to={ctaHref}>
              {isInFreePlanCountry
                ? m.app_slider_cta_free()
                : m.app_slider_cta()}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
